import React, { useState } from "react";
import {
  Checkbox,
  Form,
  Input,
  Modal,
  Radio,
  Select,
  Space,
  Typography,
  message,
} from "antd";

import {
  generateCrmApiKeyApi,
} from "../../../../../apiservice/crm";
import "../../../crmSharedStyle.css";

const { Text } = Typography;

// const DEFAULT_PERMISSIONS = {
//   leads: {
//     create: true,
//     read: true,
//     update: true,
//     notes: true,
//   },
// };
const DEFAULT_PERMISSIONS = {
  leads: {
    create: true,
    read: false,
    update: false,
    notes: false,
  },
};

const GenerateApiKeyModal = ({
  open,
  onClose,
  onGenerated,
  users = [],
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const [permissions, setPermissions] =
    useState(DEFAULT_PERMISSIONS);

  const assignmentMode = Form.useWatch("assignment_mode", form) || "specific";

  const changePermission = (action, checked) => {
    setPermissions((prev) => ({
      ...prev,

      leads: {
        ...prev.leads,
        [action]: checked,
      },
    }));
  };

  const handleCancel = () => {
    form.resetFields();

    setPermissions(DEFAULT_PERMISSIONS);

    onClose();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      setLoading(true);

      const res = await generateCrmApiKeyApi({
        name: values.name,
        permissions,
        assignment_mode: values.assignment_mode || "specific",
        default_assigned_to: values.default_assigned_to || null,
        assignment_pool: values.assignment_pool || [],
      });

      /*
       * Adjust depending on your controller response.
       *
       * Expected something like:
       *
       * {
       *   success: true,
       *   data: {
       *      id,
       *      api_key,
       *      ...
       *   }
       * }
       */

      const generated =
        res?.data?.data || res?.data;

      form.resetFields();

      setPermissions(DEFAULT_PERMISSIONS);

      onGenerated(generated);
    } catch (err) {
      if (err?.errorFields) {
        return;
      }

      message.error(
        err?.response?.data?.message ||
          "Failed to generate API key"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Generate API Key"
      open={open}
      onCancel={handleCancel}
      onOk={handleSubmit}
      okText="Generate Key"
      confirmLoading={loading}
      destroyOnClose
      className="crm-modal-responsive"
    >
      <Form
        form={form}
        layout="vertical"
        style={{ marginTop: 20 }}
      >
        <Form.Item
          label="Integration Name"
          name="name"
          rules={[
            {
              required: true,
              message:
                "Enter an integration name",
            },
          ]}
        >
          <Input
            placeholder="Example: Company Website"
            maxLength={100}
          />
        </Form.Item>

        <Form.Item
          label="Lead Assignment"
          name="assignment_mode"
          initialValue="specific"
          tooltip="How leads created through this key are assigned when the request itself doesn't specify an assignee."
        >
          <Radio.Group>
            <Space direction="vertical">
              <Radio value="specific">One person</Radio>
              <Radio value="round_robin">Round robin (rotate through selected people)</Radio>
              <Radio value="workload">Auto (workload balanced across all active agents)</Radio>
            </Space>
          </Radio.Group>
        </Form.Item>

        {assignmentMode === "specific" && (
          <Form.Item
            label="Assignee"
            name="default_assigned_to"
            tooltip="Leads go to this person. If left blank, they go to whoever generates this key."
          >
            <Select
              placeholder="Whoever generates this key (default)"
              allowClear
              showSearch
              optionFilterProp="label"
              options={users.map((u) => ({ label: u.full_name, value: u.id }))}
            />
          </Form.Item>
        )}

        {assignmentMode === "round_robin" && (
          <Form.Item
            label="Rotate Between"
            name="assignment_pool"
            tooltip="Each new lead goes to the next person in this list, in order, looping back to the start."
            rules={[
              {
                required: true,
                message: "Select at least one person to rotate between",
              },
            ]}
          >
            <Select
              mode="multiple"
              placeholder="Select agents to rotate between"
              showSearch
              optionFilterProp="label"
              options={users.map((u) => ({ label: u.full_name, value: u.id }))}
            />
          </Form.Item>
        )}

        {assignmentMode === "workload" && (
          <Text type="secondary" style={{ display: "block", marginBottom: 20 }}>
            Each lead goes to whoever among your active agents currently has the lightest load.
          </Text>
        )}

        <div style={{ marginBottom: 10 }}>
          <Text strong>Lead Permissions</Text>
        </div>

        <Space
          direction="vertical"
          style={{ width: "100%" }}
        >
          <Checkbox
            checked={permissions.leads.create}
            onChange={(e) =>
              changePermission(
                "create",
                e.target.checked
              )
            }
          >
            Create Leads
          </Checkbox>

          <Checkbox
            checked={permissions.leads.read}
            onChange={(e) =>
              changePermission(
                "read",
                e.target.checked
              )
            }
          >
            Read Lead Details
          </Checkbox>

          <Checkbox
            checked={permissions.leads.update}
            onChange={(e) =>
              changePermission(
                "update",
                e.target.checked
              )
            }
          >
            Update Leads
          </Checkbox>

          <Checkbox
            checked={permissions.leads.notes}
            onChange={(e) =>
              changePermission(
                "notes",
                e.target.checked
              )
            }
          >
            Add Lead Notes
          </Checkbox>
        </Space>
      </Form>
    </Modal>
  );
};

export default GenerateApiKeyModal;